import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSafe } from '../context/SafeContext';
import LoadingPane from '../components/LoadingPane';
import { PendingInvitesBanner } from '../components/PendingInvitesBanner';
import { useEntitlement } from '../hooks/useEntitlement';
import { ProBadge } from '../components/ProBadge';
import { ProModal } from '../components/ProModal';
import { LapsedFamilyCard } from '../components/LapsedFamilyCard';
import { AddSeatsCTA } from '../components/AddSeatsCTA';
import { getCurrentUserId } from '../api/client';
import type { Organization, OrgRole } from '@heybesafe/shared';

function RecoveryTile({ onClick }: { onClick: () => void }) {
  return (
    <button
      id="recovery-tile-button"
      onClick={onClick}
      className="w-full text-left p-5 bg-hbs-surface rounded-hbs-xl border border-hbs-border hover:border-hbs-primary/40 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-hbs-text">Recovery contacts</h3>
          <p className="text-xs text-hbs-text-mute mt-1">
            Trusted contacts who can help you regain access to your safe.
          </p>
          <p className="text-xs text-hbs-text-mute mt-1">
            In case of emergency, they can get access to your logins.
          </p>
        </div>
        <div className="flex items-center gap-1 text-xs text-hbs-text-mute">
          Manage
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </button>
  );
}

function OrgCard({
  org,
  attention,
  onClick,
}: {
  org: Organization & { role: OrgRole };
  attention: boolean;
  onClick: () => void;
}) {
  return (
    <button
      id={`org-card-${org.id}`}
      onClick={onClick}
      className="w-full text-left p-5 bg-hbs-surface rounded-hbs-xl border border-hbs-border hover:border-hbs-primary/40 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-hbs-text truncate flex items-center gap-2">
            {attention && (
              <span
                aria-label="This family needs your attention"
                className="inline-block w-2 h-2 rounded-full bg-hbs-danger flex-shrink-0"
              />
            )}
            <span className="truncate">{org.name}</span>
          </h3>
          <p className="text-xs text-hbs-text-mute mt-1">
            Created {new Date(org.createdAt).toLocaleDateString()}
          </p>
        </div>
        <span className={`flex-shrink-0 text-xs px-2 py-1 rounded-full font-medium ${
          org.role === 'admin'
            ? 'bg-hbs-primary/10 text-hbs-primary'
            : 'bg-hbs-surface-alt text-hbs-text-mute'
        }`}>
          {org.role === 'admin' ? 'Admin' : 'Member'}
        </span>
      </div>
      <div className="flex items-center gap-1 mt-3 text-xs text-hbs-text-mute">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        View members
      </div>
    </button>
  );
}

function HeaderCTA({
  canCreateFamily,
  isActiveSubscriber,
  hasOrgs,
  seatPool,
  onCreate,
  onSubscribe,
}: {
  canCreateFamily: boolean;
  isActiveSubscriber: boolean;
  hasOrgs: boolean;
  /** Pooled seat allowance across the owner's active subs; used to preface
   *  "Add seats" with the remaining count. Absent → no count shown. */
  seatPool?: { capacity: number; used: number };
  onCreate: () => void;
  onSubscribe: () => void;
}) {
  // Active subscribers can both create another family (while their seat pool
  // has room) AND add more seats — these are two distinct people-based
  // actions, not alternatives, so render both rather than picking one.
  // NOTE: the create-family button below and the one in the `canCreateFamily`
  // branch deliberately share `create-family-button`. The two branches return
  // early and are mutually exclusive, so only one is ever in the DOM — a test
  // can look up "the create-family button" without knowing which branch ran.
  if (isActiveSubscriber) {
    // Seats are pooled per ACCOUNT (across every family the owner admins), not
    // per family — so the remaining count lives here on the Families page next
    // to "Add seats", not on an individual family's Manage tab.
    const remaining = seatPool ? Math.max(0, seatPool.capacity - seatPool.used) : null;
    return (
      <div className="flex items-center gap-3 flex-wrap">
        {canCreateFamily && (
          <button
            id="create-family-button"
            onClick={onCreate}
            className="flex items-center gap-2 px-4 py-2 bg-hbs-primary hover:bg-hbs-primary-hover text-white rounded-hbs-md text-sm font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            {hasOrgs ? 'Create another family' : 'Create family'}
          </button>
        )}
        <div className="flex items-center gap-2">
          {seatPool && (
            <span className="text-sm text-hbs-text-mute" data-testid="seats-remaining">
              {remaining} of {seatPool.capacity} seats remaining
            </span>
          )}
          <AddSeatsCTA label="Add seats" variant={canCreateFamily ? 'inline' : 'primary'} />
        </div>
      </div>
    );
  }
  if (canCreateFamily) {
    return (
      <button
        id="create-family-button"
        onClick={onCreate}
        className="flex items-center gap-2 px-4 py-2 bg-hbs-primary hover:bg-hbs-primary-hover text-white rounded-hbs-md text-sm font-medium transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
        </svg>
        {hasOrgs ? 'Create another family' : 'Create family'}
      </button>
    );
  }
  return (
    <button
      id="subscribe-to-create-family-button"
      onClick={onSubscribe}
      className="flex items-center gap-2 px-4 py-2 bg-hbs-primary hover:bg-hbs-primary-hover text-white rounded-hbs-md text-sm font-medium transition-colors"
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
      </svg>
      Create family
      <ProBadge />
    </button>
  );
}

export default function OrgListPage() {
  const navigate = useNavigate();
  const { orgs, orgsLoaded, pendingInvites, pendingFinalizations, refreshOrgs } = useSafe();
  const { canCreateFamily, snapshot } = useEntitlement();
  const [proModalOpen, setProModalOpen] = useState(false);
  const isActiveSubscriber = snapshot?.state === 'active-subscriber';
  const hasOrgs = orgs.length > 0;
  const orgsWithFinalizations = new Set(pendingFinalizations.map((f) => f.orgId));
  const myUserId = getCurrentUserId();

  return (
    <div className="p-9 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display font-semibold text-[32px] tracking-hbs-tight text-hbs-text">Families</h1>
        {/* Actions sit on their own row below the title: the create-family button
            and the Add seats link (prefaced by the pooled seats-remaining count). */}
        <div className="mt-4">
          <HeaderCTA
            canCreateFamily={canCreateFamily}
            isActiveSubscriber={isActiveSubscriber}
            hasOrgs={hasOrgs}
            seatPool={snapshot?.seatPool}
            onCreate={() => navigate('/orgs/new')}
            onSubscribe={() => setProModalOpen(true)}
          />
        </div>
      </div>

      <PendingInvitesBanner invites={pendingInvites} />

      {/* orgs is emptied on lock; without orgsLoaded this renders "not a member
          of any family" for a full round-trip after every unlock. */}
      {!orgsLoaded && orgs.length === 0 && pendingInvites.length === 0 ? (
        <LoadingPane label="Loading families…" className="flex flex-col items-center justify-center py-20 text-hbs-text-mute gap-3" />
      ) : orgs.length === 0 && pendingInvites.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-hbs-text-mute gap-3">
          <svg className="w-12 h-12 text-hbs-text-mute" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-sm">You are not a member of any family.</p>
          {canCreateFamily ? (
            <button
              id="create-first-family-button"
              onClick={() => navigate('/orgs/new')}
              className="text-xs text-hbs-primary hover:text-hbs-primary transition-colors"
            >
              Create your first family
            </button>
          ) : isActiveSubscriber ? (
            <AddSeatsCTA label="Add seats to invite more people" variant="inline" />
          ) : (
            <button
              id="create-first-family-pro-button"
              onClick={() => setProModalOpen(true)}
              className="text-xs text-hbs-primary hover:text-hbs-primary transition-colors"
            >
              Create your first family<ProBadge />
            </button>
          )}
        </div>
      ) : orgs.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {orgs.map((org) => {
            // Uncovered family: the owner (adminUserId === viewer) gets a
            // resubscribe card; a non-owner member sees nothing. Covered → normal.
            if (org.covered === false) {
              return org.adminUserId === myUserId ? (
                <LapsedFamilyCard
                  key={org.id}
                  orgId={org.id}
                  familyName={org.name}
                  userIsOwner
                  onDeleted={refreshOrgs}
                />
              ) : null;
            }
            return (
              <OrgCard
                key={org.id}
                org={org}
                attention={orgsWithFinalizations.has(org.id)}
                onClick={() => navigate(`/orgs/${org.id}`)}
              />
            );
          })}
        </div>
      ) : null}

      <section className="mt-8">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-hbs-text-mute mb-3">
          Recovery
        </h2>
        <RecoveryTile onClick={() => navigate('/settings/recovery-contacts')} />
      </section>

      {proModalOpen && <ProModal feature="create-family" onClose={() => setProModalOpen(false)} />}
    </div>
  );
}
